import { AlertCircle, Home, Phone } from "lucide-react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useCcaVenuePaymentStatus } from "../hooks/useCcaVenuePaymentStatus";

function getNormalizedRegistrationType(paymentData, pathname) {
  const explicitType = String(
    paymentData?.registrationType || paymentData?.invoiceBreakdown?.meta?.registrationType || ""
  )
    .trim()
    .toUpperCase();

  if (explicitType) {
    return explicitType;
  }

  if (pathname === "/payment-failure") {
    return "INDIAN";
  }

  return "";
}

function getPaymentCurrency(paymentData) {
  const explicitCurrency = String(paymentData?.currency || "").trim().toUpperCase();
  if (explicitCurrency) {
    return explicitCurrency;
  }

  const registrationType = String(
    paymentData?.registrationType || paymentData?.invoiceBreakdown?.meta?.registrationType || ""
  )
    .trim()
    .toUpperCase();

  return registrationType === "INTERNATIONAL" ? "USD" : "INR";
}

function formatAmount(amount, currency) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return "";
  }

  return new Intl.NumberFormat(currency === "USD" ? "en-US" : "en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function PaymentFailedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paymentData = location.state || {
    registrationCode: searchParams.get("registrationCode") || "",
    transactionId: searchParams.get("transactionId") || "",
    amount: searchParams.get("amount") || "",
    error: searchParams.get("error") || "",
    formData: {
      studentName: searchParams.get("studentName") || "",
    },
  };
  const orderId = String(searchParams.get("orderId") || paymentData?.orderId || paymentData?.registrationCode || "").trim();
  const { paymentStatus, paymentData: verifiedPaymentData } = useCcaVenuePaymentStatus(orderId);
  const confirmedPaymentData = { ...paymentData, ...(verifiedPaymentData || {}) };
  useEffect(() => {
    if (paymentStatus === "SUCCESS") {
      navigate(`/register/payment/success?orderId=${encodeURIComponent(orderId)}`, { replace: true });
    }
  }, [navigate, orderId, paymentStatus]);

  if (paymentStatus === "PENDING") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl">
          <h1 className="mb-3 text-2xl font-bold text-slate-800">Payment Processing</h1>
          <p className="text-slate-600">
            CCAvenue has not confirmed a final payment status yet. We are checking again automatically.
          </p>
          {orderId && <p className="mt-4 text-sm text-slate-500">Reference: {orderId}</p>}
        </div>
      </div>
    );
  }
  const registrationType = getNormalizedRegistrationType(paymentData, location.pathname);
  const paymentCurrency = getPaymentCurrency({
    ...paymentData,
    registrationType,
  });
  const formattedAmount = formatAmount(confirmedPaymentData?.amount, paymentCurrency);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-black text-slate-800 mb-2">Payment Unsuccessful</h1>
          <p className="text-slate-600 mb-2">
            We regret to inform you that your payment for Unified Council Olympiads 2026-27 could not be processed successfully.
          </p>
          <p className="text-slate-500 mb-6">
            {confirmedPaymentData?.error || "Please try again after a few minutes."}
          </p>

          <div className="bg-slate-50 rounded-2xl p-5 mb-6 text-left space-y-2">
            {confirmedPaymentData?.registrationCode && (
              <div className="flex justify-between">
                <span className="text-slate-500">Reference</span>
                <span className="font-semibold text-slate-800">{confirmedPaymentData.registrationCode}</span>
              </div>
            )}
            {confirmedPaymentData?.formData?.studentName && (
              <div className="flex justify-between">
                <span className="text-slate-500">Student</span>
                <span className="font-semibold text-slate-800">{confirmedPaymentData.formData.studentName}</span>
              </div>
            )}
            {confirmedPaymentData?.amount && (
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-black text-red-600">{formattedAmount}</span>
              </div>
            )}
          </div>

          {formattedAmount && (
            <p className="text-left text-sm font-semibold text-slate-700 mb-4">
              Payment amount: {formattedAmount}
            </p>
          )}

          <div className="mb-6 rounded-2xl bg-slate-50 p-5 text-left text-sm leading-6 text-slate-700 space-y-3">
            <p className="font-semibold text-slate-800">This may happen due to reasons such as:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Network connectivity issues</li>
              <li>Bank or card authorization failure</li>
              <li>UPI/payment gateway timeout</li>
              <li>Transaction cancellation before completion</li>
            </ul>
            <p>Please try making the payment again after a few minutes.</p>
            <p>
              If the payment amount has been deducted from your account but the registration status is not confirmed,
              please wait for some time as banks may take a while to update the transaction status.
            </p>
            <p>
              For assistance, please contact our support team on 9030007882 | 9603064939 with your transaction
              details.
            </p>
            <p>Thank you for your patience and understanding.</p>
            <p className="font-semibold text-slate-800">Team Unified Council</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={() => navigate("/options")} className="flex-1 rounded-full py-6">
              Back to Register
            </Button>
          </div>

          <Button
            type="button"
            onClick={() => {
              window.location.href = "tel:+919603064939";
            }}
            className="mt-4 w-full rounded-full py-6 bg-slate-900 hover:bg-slate-800"
          >
            <Phone className="w-4 h-4 mr-2" />
            Call our Expert Support
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mt-4 rounded-full text-slate-600"
          >
            <Home className="w-4 h-4 mr-2" />
            Go to Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentFailedPage;
